import { Heading1Icon } from "lucide-react";
import { useCallback } from "react";
import {
  T,
  TLBaseShape,
  ShapeUtil,
  Rectangle2d,
  Editor,
  TLResizeInfo,
  resizeBox,
} from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  BlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  NodeDocsDescription,
  NodeDocsHeader,
} from "@/components/canvas-editor/NodeDocs";
import {
  Dimensions,
  metadataType,
  notesType,
} from "@/components/canvas-editor/shapes/shapes";
import { preventBubble, ToolStateNode } from "@/components/canvas-editor/utils";
import { useActiveTool } from "@/components/hooks/useCustomActiveTool";
import { RichTextEditor } from "@/components/RichTextEditor";
import { NodeMetadata } from "@/lib/types";

// Define the shape type

// Define the shape's properties
type NotesProps = {
  w: number;
  h: number;
  body: string;
  metadata: NodeMetadata;
};

// Define the shape
type NotesShape = TLBaseShape<typeof notesType, NotesProps>;

function NotesDocs() {
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>{NotesUtil.docs.title}</NodeDocsHeader>
      <NodeDocsDescription>
        Add notes and comments to your canvas to document your feed design.
      </NodeDocsDescription>
    </div>
  );
}

export class NotesTool extends ToolStateNode {
  static override id = notesType;

  static override uiToolDef = (editor: Editor) => ({
    id: notesType,
    icon: Heading1Icon,
    label: "Markdown Note",
    kbd: "m",
    defaultColor: "default" as const,
    onSelect: () => editor.setCurrentTool(notesType),
    docs: () => <NotesDocs />,
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: notesType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

const MIN_H = 64;
const MIN_W = 200;

// Create the shape util
export class NotesUtil extends ShapeUtil<NotesShape> {
  static override type = notesType;

  static docs = {
    title: "Notes",
    description: "Add notes to document your feed design",
    defaultColor: "amber" as const,
  };

  static override props = {
    w: T.number,
    h: T.number,
    body: T.string,
    metadata: metadataType,
  };

  override canResize() {
    return true;
  }
  override onResize(shape: NotesShape, info: TLResizeInfo<NotesShape>) {
    info.scaleY = 1;
    return resizeBox(shape, info);
  }

  getDefaultProps(): NotesProps {
    return {
      w: MIN_W,
      h: MIN_H,
      body: "",
      metadata: {},
    };
  }

  updateShape(
    shape: NotesShape,
    updates: Pick<Dimensions, "h"> | { body?: string },
  ) {
    this.editor.updateShape({
      id: shape.id,
      type: shape.type,
      props: {
        ...shape.props,
        ...updates,
      },
    });
  }

  component(shape: NotesShape) {
    return <Notes shape={shape} updateShape={this.updateShape.bind(this)} />;
  }

  indicator(shape: NotesShape) {
    return (
      <rect
        width={Math.max(shape.props.w, MIN_W)}
        height={Math.max(shape.props.h, MIN_H)}
      />
    );
  }

  getGeometry(shape: NotesShape) {
    return new Rectangle2d({
      width: Math.max(shape.props.w, MIN_W),
      height: Math.max(shape.props.h, MIN_H),
      isFilled: true,
    });
  }
}

function Notes({
  shape,
  updateShape,
}: {
  shape: NotesShape;
  updateShape: (
    shape: NotesShape,
    updates: Pick<Dimensions, "h"> | { body?: string },
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );
  const onUpdateBody = useCallback(
    (body: string) => {
      updateShape(shape, { body });
    },
    [shape, updateShape],
  );

  const activeTool = useActiveTool();
  const isArrow = activeTool === "arrow";

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        variant={shape.props.metadata.color}
        width={Math.max(shape.props.w, MIN_W)}
        height={Math.max(shape.props.h, MIN_H)}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <BlockHeader description="Record some notes about your feed. Markdown supported">
          {shape.props.metadata.title ?? "Notes"}
        </BlockHeader>
        <BlockBody
          className="px-3 py-2 pointer-events-auto relative"
          {...(isArrow ? {} : preventBubble)}
        >
          <RichTextEditor value={shape.props.body} onChange={onUpdateBody} />
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
